import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Heart, Sparkles, Shield, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { quizLeadSchema, getValidationError } from "@/lib/validation";
import SkipToContent from "@/components/SkipToContent";

type QuizStep = "hero" | "q1" | "q2" | "q3" | "lead" | "success";

interface Answer {
  q1?: string;
  q2?: string;
  q3?: string;
}

const QuizWomenPage = () => {
  const [step, setStep] = useState<QuizStep>("hero");
  const [answers, setAnswers] = useState<Answer>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Honeypot field for bot detection
  const [honeypot, setHoneypot] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleAnswer = (question: keyof Answer, answer: string) => {
    setAnswers((prev) => ({ ...prev, [question]: answer }));
    
    if (question === "q1") setStep("q2");
    if (question === "q2") setStep("q3");
    if (question === "q3") setStep("lead");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Bot detection: if honeypot field is filled, silently reject
    if (honeypot) {
      setStep("success");
      return;
    }
    
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const name = formData.get("name") as string;
    const phone = formData.get("phone") as string;
    const email = formData.get("email") as string;

    // Validate form data with Zod schema
    const validationResult = quizLeadSchema.safeParse({
      name,
      phone,
      email,
      quiz_q1: answers.q1 || null,
      quiz_q2: answers.q2 || null,
      quiz_q3: answers.q3 || null,
    });

    if (!validationResult.success) {
      const errorMessage = getValidationError(validationResult);
      toast({
        title: "שגיאה",
        description: errorMessage || "נא לבדוק את הפרטים שהזנת",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Save validated data to Supabase
      const { error } = await supabase.from("leads").insert({
        name: validationResult.data.name,
        phone: validationResult.data.phone,
        email: validationResult.data.email,
        source_page: "quiz_women",
        quiz_q1: validationResult.data.quiz_q1,
        quiz_q2: validationResult.data.quiz_q2,
        quiz_q3: validationResult.data.quiz_q3,
      });

      if (error) throw error;
      setStep("success");
    } catch (error) {
      toast({
        title: "שגיאה",
        description: "אירעה שגיאה בשליחת הטופס. נסי שוב.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case "hero":
        return (
          <div key="hero" className="text-center space-y-8">
            <div className="inline-flex items-center gap-2 bg-gold/20 text-gold-dark px-4 py-2 rounded-full text-sm font-medium opacity-0 animate-fade-in-scale">
              <Heart className="w-4 h-4" />
              <span>מבדק DNA זוגי</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-serif text-navy leading-tight opacity-0 animate-fade-up" style={{ animationDelay: '0.15s' }}>
              גלי את ה-DNA הזוגי שלך
            </h1>
            
            <p className="text-xl md:text-2xl text-navy/70 max-w-xl mx-auto leading-relaxed opacity-0 animate-fade-up" style={{ animationDelay: '0.3s' }}>
              למה את מושכת את הגברים הלא נכונים?
              <br />
              <span className="text-gold-dark font-medium">(מבדק אבחון קצר)</span>
            </p>
            
            <div className="opacity-0 animate-fade-up" style={{ animationDelay: '0.45s' }}>
              <Button
                onClick={() => setStep("q1")}
                className="h-16 px-12 text-xl font-bold bg-gold hover:bg-gold-light text-navy btn-glow transition-all duration-300 hover:scale-105"
              >
                התחילי אבחון
                <ArrowLeft className="mr-2 w-6 h-6" />
              </Button>
            </div>
            
            <p className="text-navy/50 text-sm opacity-0 animate-fade-up" style={{ animationDelay: '0.6s' }}>
              ⏱️ 60 שניות בלבד • 🔒 100% דיסקרטי
            </p>
          </div>
        );

      case "q1":
        return (
          <QuestionScreen
            key="q1"
            questionNumber={1}
            totalQuestions={3}
            question="כשאת מסתכלת לאחור, מה הדפוס שחוזר בקשרים שלך?"
            icon={<Sparkles className="w-8 h-8 text-gold-dark" />}
            options={[
              { id: "a", text: 'הילד הנצחי - אני תמיד מוצאת את עצמי "מגדלת" אותם.' },
              { id: "b", text: "הנעלם - הכל מתחיל אש, ואז הוא נבהל ומתרחק." },
              { id: "c", text: "המשעמם - הם בחורים טובים, אבל אין לי משיכה אליהם." },
            ]}
            onSelect={(answer) => handleAnswer("q1", answer)}
          />
        );

      case "q2":
        return (
          <QuestionScreen
            key="q2"
            questionNumber={2}
            totalQuestions={3}
            question="מה הדבר שהכי חסר לך ביום-יום בזוגיות?"
            icon={<Heart className="w-8 h-8 text-gold-dark" />}
            options={[
              { id: "a", text: "גב וביטחון - מישהו שאפשר להישען עליו." },
              { id: "b", text: "הקשבה - מישהו שבאמת רואה אותי." },
              { id: "c", text: "דרייב משותף - פרטנר לטרוף איתו את העולם." },
            ]}
            onSelect={(answer) => handleAnswer("q2", answer)}
          />
        );

      case "q3":
        return (
          <QuestionScreen
            key="q3"
            questionNumber={3}
            totalQuestions={3}
            question="מה מונע ממך להיכנס לאפליקציות כרגע?"
            icon={<Shield className="w-8 h-8 text-gold-dark" />}
            options={[
              { id: "a", text: "חוסר אמינות - הפחד ממתחזים ושקרנים." },
              { id: "b", text: "שחיקה - אין לי כוח לספר את הסיפור שלי שוב." },
              { id: "c", text: "התפשרות - הפחד שאצטרך להוריד סטנדרטים." },
            ]}
            onSelect={(answer) => handleAnswer("q3", answer)}
          />
        );

      case "lead":
        return (
          <div key="lead" className="text-center space-y-8 max-w-md mx-auto">
            <div className="inline-flex items-center gap-2 bg-gold/20 text-gold-dark px-4 py-2 rounded-full text-sm font-medium opacity-0 animate-fade-in-scale">
              <CheckCircle2 className="w-4 h-4" />
              <span>הפרופיל הרגשי שלך הושלם</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-serif text-navy leading-tight opacity-0 animate-fade-up" style={{ animationDelay: '0.1s' }}>
              כדי לראות איזה גבר מתאים ל-DNA שלך
            </h2>
            
            <p className="text-lg text-navy/70 opacity-0 animate-fade-up" style={{ animationDelay: '0.2s' }}>
              הזיני פרטים לקבלת התוצאות
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="hidden" name="source" value="quiz_women" />
              {/* Honeypot field - hidden from users, bots will fill it */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                className="absolute opacity-0 pointer-events-none h-0 w-0"
                aria-hidden="true"
              />
              <div className="opacity-0 animate-slide-in-right" style={{ animationDelay: '0.3s' }}>
                <label htmlFor="quiz-women-name" className="sr-only">שם מלא</label>
                <Input
                  id="quiz-women-name"
                  type="text"
                  name="name"
                  placeholder="שם מלא"
                  required
                  maxLength={100}
                  aria-required="true"
                  className="bg-cream-dark/50 border-gold/40 text-navy placeholder:text-navy/50 h-14 text-lg backdrop-blur-sm focus:border-gold focus:ring-gold"
                />
              </div>
              <div className="opacity-0 animate-slide-in-right" style={{ animationDelay: '0.4s' }}>
                <label htmlFor="quiz-women-phone" className="sr-only">מספר טלפון</label>
                <Input
                  id="quiz-women-phone"
                  type="tel"
                  name="phone"
                  placeholder="מספר טלפון"
                  required
                  maxLength={12}
                  aria-required="true"
                  className="bg-cream-dark/50 border-gold/40 text-navy placeholder:text-navy/50 h-14 text-lg backdrop-blur-sm focus:border-gold focus:ring-gold"
                />
              </div>
              <div className="opacity-0 animate-slide-in-right" style={{ animationDelay: '0.5s' }}>
                <label htmlFor="quiz-women-email" className="sr-only">כתובת אימייל</label>
                <Input
                  id="quiz-women-email"
                  type="email"
                  name="email"
                  placeholder="כתובת אימייל"
                  required
                  maxLength={255}
                  aria-required="true"
                  className="bg-cream-dark/50 border-gold/40 text-navy placeholder:text-navy/50 h-14 text-lg backdrop-blur-sm focus:border-gold focus:ring-gold"
                />
              </div>
              <div className="opacity-0 animate-fade-up" style={{ animationDelay: '0.6s' }}>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-16 text-xl font-bold bg-navy hover:bg-navy-light text-cream shadow-elegant transition-all duration-300 disabled:opacity-50"
                >
                  {isSubmitting ? "שולחת..." : "גלי את ההתאמות שלי"}
                </Button>
              </div>
            </form>
            
            <p className="text-navy/50 text-xs opacity-0 animate-fade-up" style={{ animationDelay: '0.7s' }}>
              🔒 הפרטים שלך מוגנים ולא יועברו לצד שלישי
            </p>
          </div>
        );

      case "success":
        return (
          <div key="success" className="text-center space-y-8">
            <div className="w-24 h-24 bg-gold/20 rounded-full flex items-center justify-center mx-auto success-icon success-glow">
              <Heart className="w-12 h-12 text-gold-dark" />
            </div>
            
            <h2 className="text-3xl md:text-4xl font-serif text-navy leading-tight opacity-0 animate-fade-up" style={{ animationDelay: '0.3s' }}>
              מעולה! הניתוח שלך נשלח לעיבוד.
            </h2>
            
            <div className="text-lg text-navy/70 max-w-lg mx-auto opacity-0 animate-fade-up text-right space-y-4" style={{ animationDelay: '0.4s' }}>
              <p>האלגוריתם שלנו מנתח כעת את התשובות שלך.</p>
              <p>התוצאות המלאות וניתוח הטיפוס שלך נשלחו אליך למייל.</p>
            </div>
            
            <div className="bg-gold/10 border border-gold/40 rounded-xl p-6 max-w-lg mx-auto opacity-0 animate-fade-in-scale text-right" style={{ animationDelay: '0.5s' }}>
              <p className="text-gold-dark font-medium text-lg mb-3">
                מה קורה עכשיו?
              </p>
              <p className="text-navy/70 leading-relaxed">
                כדי לשמור על איכות המאגר, הכניסה אליו מותנית ב<strong className="text-navy">שיחת איפיון ובדיקת התאמה.</strong>
              </p>
              <p className="text-navy/70 leading-relaxed mt-3">
                אחד מהמאמנים שלנו ייצור איתך קשר בקרוב כדי לבחון את התאמתך לתהליך ולקבלת גישה למאגר.
              </p>
              <p className="text-navy/70 leading-relaxed mt-3">
                בינתיים, שריינו לך עדיפות בתור לשיחה.
              </p>
            </div>
            
            {/* 50/30/20 Model Explanation */}
            <div className="bg-gold/10 border border-gold/40 rounded-xl p-6 max-w-lg mx-auto opacity-0 animate-fade-in-scale text-right" style={{ animationDelay: '0.55s' }}>
              <p className="text-gold-dark font-medium text-lg mb-3">
                למה השיטה שלנו מצליחה איפה שטינדר נכשל?
              </p>
              <p className="text-navy/70 leading-relaxed">
                אפליקציות בנויות על כימיה ויזואלית (משיכה רגעית). זה מחזיק שבועיים.
              </p>
              <p className="text-navy/70 leading-relaxed mt-3">
                המערכת שלנו בנויה על מודל ה-50/30/20:
              </p>
              <ul className="text-navy/70 leading-relaxed mt-2 space-y-1 pr-4">
                <li><strong className="text-gold-dark">50%</strong> התאמה רגשית (ניהול כעסים, אנרגיה בבית)</li>
                <li><strong className="text-gold-dark">30%</strong> ערכים (כסף, משפחה, דת)</li>
                <li><strong className="text-gold-dark">20%</strong> משיכה והעדפות</li>
              </ul>
              <p className="text-navy/70 leading-relaxed mt-3">
                בשיחת ההתאמה שנקיים, אנחנו לא נשאל אותך איך הצד השני צריך להיראות, אלא נבדוק את ה-50% הקריטיים שהפילו לך את הקשרים הקודמים.
              </p>
            </div>
            
            <div className="opacity-0 animate-fade-up" style={{ animationDelay: '0.6s' }}>
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="border-navy/30 text-navy hover:bg-navy/5 h-14 px-8 text-lg"
              >
                חזרה לדף הבית
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <Helmet>
        <title>מבדק DNA זוגי לנשים | אמון</title>
        <meta
          name="description"
          content="גלי את ה-DNA הזוגי שלך ב-60 שניות. מבדק אישיות זוגית לנשים שרוצות להבין למה הן מושכות את הגברים הלא נכונים."
        />
      </Helmet>
      <SkipToContent />
      <main id="main-content" role="main" tabIndex={-1} className="min-h-screen bg-cream relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-cream via-cream-dark/30 to-cream" />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-navy/5 rounded-full blur-3xl" />
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-2 h-2 bg-gold rounded-full opacity-60" />
        <div className="absolute top-40 right-20 w-3 h-3 bg-gold rounded-full opacity-40" />
        <div className="absolute bottom-40 left-1/3 w-2 h-2 bg-gold rounded-full opacity-50" />
        
        {/* Content */}
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-2xl">
            {renderStep()}
          </div>
        </div>
        
        {/* Progress bar */}
        {step !== "hero" && step !== "success" && (
          <div 
            className="fixed bottom-0 left-0 right-0 h-1 bg-cream-dark"
            role="progressbar"
            aria-valuenow={step === "q1" ? 25 : step === "q2" ? 50 : step === "q3" ? 75 : 100}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="התקדמות במבדק"
          >
            <div
              className="h-full bg-gradient-to-l from-gold to-gold-light transition-all duration-500"
              style={{
                width:
                  step === "q1"
                    ? "25%"
                    : step === "q2"
                    ? "50%"
                    : step === "q3"
                    ? "75%"
                    : "100%",
              }}
            />
          </div>
        )}
      </main>
    </>
  );
};

interface QuestionScreenProps {
  questionNumber: number;
  totalQuestions: number;
  question: string;
  icon: React.ReactNode;
  options: { id: string; text: string }[];
  onSelect: (answer: string) => void;
}

const QuestionScreen = ({
  questionNumber,
  totalQuestions,
  question,
  icon,
  options,
  onSelect,
}: QuestionScreenProps) => {
  return (
    <div className="space-y-8">
      {/* Question header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-2 text-navy/60 text-sm opacity-0 animate-fade-up">
          <span>שאלה {questionNumber} מתוך {totalQuestions}</span>
        </div>
        <div className="flex justify-center icon-bounce">{icon}</div>
        <h2 className="text-2xl md:text-3xl font-serif text-navy leading-relaxed opacity-0 animate-fade-up" style={{ animationDelay: '0.15s' }}>
          {question}
        </h2>
      </div>

      {/* Options with staggered animation */}
      <div className="space-y-4">
        {options.map((option, index) => (
          <button
            key={option.id}
            onClick={() => onSelect(option.text)}
            className={`stagger-item stagger-${index + 1} w-full text-right p-6 bg-cream-dark/50 hover:bg-cream-dark border border-gold/20 hover:border-gold/50 rounded-xl text-navy transition-all duration-300 hover:scale-[1.02] hover:shadow-elegant group`}
          >
            <div className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gold/20 text-gold-dark flex items-center justify-center text-sm font-bold group-hover:bg-gold group-hover:text-navy transition-colors">
                {option.id.toUpperCase()}
              </span>
              <span className="text-lg leading-relaxed">{option.text}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default QuizWomenPage;